import {
  countLabel,
  microHeading,
  microHeadingCss,
  panel,
  panelCss,
  rowList,
  rowsCss,
  totalOf,
  totalsCss,
  totalsRow,
} from '../armKit';
import { bookmarkCounts, mentionCounts } from '../batchFetchers';

import type { ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Read = { workId: number; title: string; url: string; views: number };

const TITLE = "Who's reading you";
// Only all-time views are reachable from this page, so the heading says so
// rather than implying a recent window the number does not describe.
const MOST_READ = 'Most read, all time';
const NOTE = 'Across the papers below.';
const MOST_READ_SIZE = 3;
const PAPER_SOURCE = 'profile-liautotest-readers-paper';

function armCss(scope: string): string {
  return (
    panelCss(scope, { marginBottom: '0' }) +
    totalsCss(scope, { marginTop: '10px' }) +
    rowsCss(scope, { metaOnPhone: true }) +
    microHeadingCss(scope, { margin: '20px 0 6px' })
  );
}

function mostReadOf(
  cards: ProfileCard[],
  facts: Map<number, WorkFacts>,
): Read[] {
  return cards
    .flatMap((card) => {
      const url = card.url;
      const views = facts.get(card.workId)?.views;
      if (url == null || views == null || views <= 0) return [];

      return [{ workId: card.workId, title: card.title, url, views }];
    })
    .sort((left, right) => right.views - left.views)
    .slice(0, MOST_READ_SIZE);
}

export const readersFirstScreen: ArmSpec = {
  onReady: async (ctx) => {
    const cards = ctx.cards();
    if (cards.length === 0) return;

    const workIds = cards.map((card) => card.workId);
    const [readers, mentions, facts] = await Promise.all([
      bookmarkCounts(workIds),
      mentionCounts(workIds),
      ctx.workFacts(),
    ]);

    const totals = [
      countLabel(totalOf(readers), 'reader'),
      countLabel(totalOf(mentions), 'mention'),
    ].filter((total): total is string => total != null);
    const reads = mostReadOf(cards, facts);

    // The long-tail author has none of the three. Telling them so in a panel of
    // its own is worse than control, so the arm keeps quiet instead.
    if (totals.length === 0 && reads.length === 0) return;

    const slot = ctx.mountSlot('columnTop');
    if (slot == null) return;

    ctx.injectStyles(armCss(ctx.scope));

    const { root, body } = panel({ title: TITLE });

    if (totals.length > 0) {
      body.appendChild(totalsRow(totals, NOTE));
    }

    if (reads.length > 0) {
      body.appendChild(microHeading(MOST_READ));
      body.appendChild(
        rowList(
          reads.map((read, index) => ({
            rank: index + 1,
            title: read.title,
            url: read.url,
            meta: countLabel(read.views, 'view') ?? undefined,
            onClick: () => ctx.track(PAPER_SOURCE, { entityId: read.workId }),
          })),
        ),
      );
    }

    slot.appendChild(root);
  },
};
